import { create } from "zustand";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { CheckCircleOutline, Undo } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import ApiService from "@/api/ApiService";
import { syncOperation, syncRowHasWarning, syncRowMessage, syncSummary, syncVehicleLabel, syncWarnings } from "./transportSyncPresentation";
import type { RestoredAssignment, SyncOperation, SyncReport, SyncRow } from "./transportSyncPresentation";

const requiresTransfer = (status?: string) => status === "ACCEPTED" || status === "DELETED";
const useFeedbackStore = create<{ open: boolean; report: SyncReport | null; error: string | null; operation: SyncOperation | null; returnFocus: HTMLElement | null }>(() => ({ open: false, report: null, error: null, operation: null, returnFocus: null }));
const focusedElement = () => typeof document !== "undefined" && document.activeElement instanceof HTMLElement ? document.activeElement : null;

function RestoreAssignmentButton({ row, orderId }: { row: SyncRow; orderId: number }) {
  const [expired, setExpired] = useState(false);
  const token = row.restore?.token;
  const expiresAt = row.restore?.expiresAt;
  useEffect(() => {
    if (!expiresAt) return;
    const delay = expiresAt * 1000 - Date.now();
    setExpired(delay <= 0);
    const timer = window.setTimeout(() => setExpired(true), Math.max(0, delay));
    return () => window.clearTimeout(timer);
  }, [expiresAt]);
  const mutation = useMutation<RestoredAssignment, unknown, string>({
    mutationFn: ApiService.restoreTransportAssignment(orderId, row.orderId),
    onSuccess: (answer, usedToken) => useFeedbackStore.setState((current) => {
      if (current.report?.bobReference !== orderId || !current.report.rows.some((item) => item.orderId === row.orderId && item.restore?.token === usedToken)) return current;
      return { report: { ...current.report, open: Math.min(current.report.open, answer.open), rows: current.report.rows.map((item) => item.orderId === row.orderId ? answer.row : item) } };
    }),
  });
  const restored = row.action === "restored" || mutation.isSuccess;
  const error = mutation.error instanceof Error ? mutation.error.message : "Wiederherstellen fehlgeschlagen. Bitte erneut versuchen.";
  return <Box sx={{ mt: 0.5 }}>
    <Button size="small" variant="outlined" color={restored ? "success" : "primary"}
      sx={restored ? { "&.Mui-disabled": { color: "success.main", borderColor: "success.light" } } : undefined}
      disabled={restored || mutation.isPending || expired || !token}
      startIcon={mutation.isPending ? <CircularProgress size={14} /> : restored ? <CheckCircleOutline /> : <Undo />}
      onClick={() => { if (token) mutation.mutate(token); }}>
      {mutation.isPending ? "Wird wiederhergestellt…" : restored ? "Rückgängig gemacht" : expired ? "Frist abgelaufen" : "Trotzdem verwenden"}
    </Button>
    {mutation.isError && <Alert severity="error" sx={{ mt: 0.5 }}>{error}</Alert>}
  </Box>;
}

export function useTransportSyncFeedback() {
  return {
    onSuccess: (answer: { data?: { sync?: SyncReport } }, status?: string) => {
      const result = answer.data?.sync ?? null;
      if (!result && !requiresTransfer(status)) return;
      useFeedbackStore.setState({ open: true, report: result, error: null, operation: syncOperation(result, status), returnFocus: focusedElement() });
    },
    onError: (failure: unknown, status?: string) => {
      if (!requiresTransfer(status)) return;
      useFeedbackStore.setState({ open: true, report: null, operation: syncOperation(null, status), error: failure instanceof Error ? failure.message : "Übertragung nach Transplan fehlgeschlagen. Status wurde nicht geändert.", returnFocus: focusedElement() });
    },
  };
}

// Lives outside the edited row, so refreshed/deleted calendar rows cannot dismiss the report.
export default function TransportSyncFeedbackDialog() {
  const { open, report, error, operation, returnFocus } = useFeedbackStore();
  // Keep the result visible throughout the closing animation.
  const close = () => useFeedbackStore.setState({ open: false });
  const summary = syncSummary(report, operation ?? "updated");
  return (
    <Dialog
      open={open} onClose={close} maxWidth="sm" fullWidth onClick={(event) => event.stopPropagation()}
      disableRestoreFocus
      // MUI's default focus() can scroll an enclosing list back to an earlier control.
      TransitionProps={{ onExited: () => { if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true }); } }}
    >
      <DialogTitle>{error ? operation === "cancelled" ? "Stornierung fehlgeschlagen" : "Übertragung fehlgeschlagen" : summary.title}</DialogTitle>
      <DialogContent>
        {error ? <Alert severity="error">{error}</Alert> : <>
          <Alert severity={summary.severity} sx={{ mb: report?.rows.length ? 1 : 0 }}>{summary.message}</Alert>
          {report && <>
            {syncWarnings(report).map((warning) => <Alert severity="warning" key={warning} sx={{ mt: 1 }}>{warning}</Alert>)}
            {report.rows.map((row) => <Box key={row.orderId} sx={{ py: 1, "& + &": { borderTop: "1px solid", borderColor: "divider" } }}>
              <Typography variant="body2" fontWeight={600}>{syncVehicleLabel(row)}</Typography>
              <Typography variant="body2" color={syncRowHasWarning(row) ? "warning.main" : "text.secondary"}>{syncRowMessage(row)}</Typography>
              {report.bobReference && (row.restore || row.action === "restored") ? <RestoreAssignmentButton key={row.restore?.token ?? "restored"} row={row} orderId={report.bobReference} /> : null}
            </Box>)}
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>Noch zu disponieren: {report.open} Fahrzeug{report.open === 1 ? "" : "e"}.</Typography>
          </>}
        </>}
      </DialogContent>
      <DialogActions><Button onClick={close}>Schließen</Button></DialogActions>
    </Dialog>
  );
}
